/* ============================================
   Hosting de la cartera — lo que el partner le vendió a sus clientes

   Es la tercera lectura de `hosting_pedidos` y la única que le faltaba: el
   módulo de admin (`/hosting`) es nuestro y es solo-admin, y `/partner/hosting`
   es el propio del partner — el que NOS compró. Lo que no existía era la lista
   de lo que ÉL le vendió a un cliente, que es trabajo suyo y no nuestro: un
   vencimiento que se le pasa lo paga su cliente, no nosotros.

   Por eso la pantalla está ordenada por vencimiento y no por fecha de alta: lo
   que se viene es lo que hay que mirar, y lo que ya venció es lo urgente.
   ============================================ */
import { useMemo, useState } from 'react'
import { useCachedData } from '@/hooks/useCachedData'
import { useApi } from '@/hooks/useApi'
import { formatCurrency, formatDate } from '@/lib/utils'
import { Badge, KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import EmptyState from '@/components/shared/EmptyState'
import { IconSearch, IconServer } from '@/components/icons/Icons'

const CRUMBS = [{ label: 'Operaciones' }, { label: 'Hosting' }]
const TODOS = 'todos'

interface PedidoCartera {
  id: number
  codigo: string | null
  cliente_id: number | null
  cliente_empresa: string | null
  plan_nombre: string | null
  periodo: string | null
  moneda: string | null
  precio_mensual: string | number | null
  total: string | number | null
  estado: string
  estado_provisioning: string | null
  dominio: string | null
  fecha_vencimiento: string | null
  renovacion_auto: boolean | null
}

const ESTADO: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente:  { label: 'Pendiente',  variant: 'warning' },
  pagado:     { label: 'Pagado',     variant: 'info' },
  activo:     { label: 'Activo',     variant: 'activo' },
  suspendido: { label: 'Suspendido', variant: 'danger' },
  vencido:    { label: 'Vencido',    variant: 'danger' },
}

/** Días hasta el vencimiento. Negativo = ya venció. */
function diasHasta(fecha: string | null): number | null {
  if (!fecha) return null
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  const vto = new Date(`${fecha.slice(0, 10)}T00:00:00`)
  return Math.round((vto.getTime() - hoy.getTime()) / 86_400_000)
}

/**
 * El vencimiento dicho como lo diría una persona. Una fecha sola obliga a
 * contar los días con los dedos, que es justo lo que hace que uno se entere
 * tarde.
 */
function vencimientoTexto(dias: number | null): { texto: string; variant: BadgeVariant } | null {
  if (dias == null) return null
  if (dias < 0) return { texto: `Venció hace ${-dias} ${-dias === 1 ? 'día' : 'días'}`, variant: 'danger' }
  if (dias === 0) return { texto: 'Vence hoy', variant: 'danger' }
  if (dias === 1) return { texto: 'Vence mañana', variant: 'warning' }
  if (dias <= 30) return { texto: `En ${dias} días`, variant: 'warning' }
  return { texto: `En ${dias} días`, variant: 'neutral' }
}

export default function PartnerHostingCartera() {
  const api = useApi()
  const { data: pedidos = [], isLoading: loading } = useCachedData(
    ['partner-hosting-cartera'],
    () => api.get<PedidoCartera[]>('/api/hosting/cartera'),
  )

  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState(TODOS)

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    return pedidos.filter(p => {
      if (filtroEstado !== TODOS && p.estado !== filtroEstado) return false
      if (!q) return true
      return [p.dominio, p.cliente_empresa, p.plan_nombre, p.codigo]
        .some(campo => campo?.toLowerCase().includes(q))
    })
  }, [pedidos, search, filtroEstado])

  const firmaFiltros = `${search}|${filtroEstado}`
  const { page, setPage, totalPages, paginated, pageSize, total } = usePagination(filtrados, 15, firmaFiltros)

  const resumen = useMemo(() => {
    const activos = pedidos.filter(p => p.estado === 'activo').length
    const porVencer = pedidos.filter(p => {
      const d = diasHasta(p.fecha_vencimiento)
      return d != null && d >= 0 && d <= 30
    }).length
    const vencidos = pedidos.filter(p => {
      const d = diasHasta(p.fecha_vencimiento)
      return d != null && d < 0
    }).length
    return { activos, porVencer, vencidos }
  }, [pedidos])

  const hayFiltros = search.trim() !== '' || filtroEstado !== TODOS
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado(TODOS) }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando hosting...</p></div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="partner-hosting-cartera">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === pedidos.length
          ? `${pedidos.length} ${pedidos.length === 1 ? 'servicio' : 'servicios'}`
          : `${total} de ${pedidos.length}`}
      />

      {pedidos.length > 0 && (
        <KpiStrip>
          <KpiRow>
            <StatCard label="Activos" value={resumen.activos} tone="success" />
            <StatCard label="Vencen en 30 días" value={resumen.porVencer} tone={resumen.porVencer > 0 ? 'warning' : 'default'} />
            <StatCard label="Vencidos" value={resumen.vencidos} tone={resumen.vencidos > 0 ? 'danger' : 'default'} />
          </KpiRow>
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            className="form-input"
            placeholder="Buscar por dominio, empresa o plan..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 170 }}>
          <option value={TODOS}>Todo estado</option>
          {Object.entries(ESTADO).map(([valor, { label }]) => (
            <option key={valor} value={valor}>{label}</option>
          ))}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {pedidos.length === 0 ? (
        <EmptyState
          icon={<IconServer size={48} />}
          title="Sin hosting en tu cartera"
          description="Acá aparece el hosting de las empresas de tu cartera, ordenado por vencimiento. Cuando una de ellas contrate el primero lo vas a ver acá, con cuánto le queda."
        />
      ) : filtrados.length === 0 ? (
        <div className="empty-state">
          <h3>Ningún servicio coincide</h3>
          <p>Prueba con otros filtros.</p>
          <button className="btn btn-secondary" onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Dominio</th>
                  <th>Empresa</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(p => {
                  const estado = ESTADO[p.estado] ?? { label: p.estado, variant: 'neutral' as BadgeVariant }
                  const vto = vencimientoTexto(diasHasta(p.fecha_vencimiento))
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="cell-stack">
                          <span className="cell-stack__title">{p.dominio || '—'}</span>
                          {p.codigo && <span className="cell-stack__sub">{p.codigo}</span>}
                        </div>
                      </td>
                      <td>{p.cliente_empresa || '—'}</td>
                      <td>{p.plan_nombre || '—'}</td>
                      <td><Badge variant={estado.variant}>{estado.label}</Badge></td>
                      <td>
                        {p.fecha_vencimiento ? (
                          <div className="cell-stack">
                            <span className="cell-stack__title">{formatDate(p.fecha_vencimiento)}</span>
                            {vto && <span className="cell-stack__sub"><Badge variant={vto.variant}>{vto.texto}</Badge></span>}
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {p.total != null
                          ? formatCurrency(Number(p.total), p.moneda || 'PEN')
                          : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page} totalPages={totalPages} total={total} pageSize={pageSize}
            onPage={setPage} noun="servicios"
          />
        </>
      )}
    </div>
  )
}
